import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiManagementApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Key, Copy, Trash2, BarChart3 } from "lucide-react";
import { formatDistance } from "date-fns";
import { ru } from "date-fns/locale";
import type { ApiKey, ApiUsageStats, ApiKeyUsageStats, ApiKeyCreate } from "@/types/api-v2";

export default function ApiManagement() {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showKeyValue, setShowKeyValue] = useState<number | null>(null);
  const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("keys");
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ isOpen: boolean; keyId: number | null; keyName: string }>({
    isOpen: false,
    keyId: null,
    keyName: ''
  });
  const [createdKeys, setCreatedKeys] = useState<{ [key: number]: string }>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch API keys
  const { data: apiKeys = [], isLoading } = useQuery<ApiKey[]>({
    queryKey: ["api-keys"],
    queryFn: () => apiManagementApi.listKeys(),
  });

  // Fetch API usage stats
  const { data: usageStats } = useQuery<ApiUsageStats>({
    queryKey: ["api-keys", "usage-stats"],
    queryFn: () => apiManagementApi.getUsageStats(),
  });

  // Fetch individual key stats
  const { data: keyStats } = useQuery<ApiKeyUsageStats>({
    queryKey: ["api-keys", selectedKeyId, "stats"],
    queryFn: () => apiManagementApi.getKeyStats(selectedKeyId!),
    enabled: !!selectedKeyId,
  });

  // Create API key mutation
  const createKeyMutation = useMutation({
    mutationFn: async (data: ApiKeyCreate) => {
      return await apiManagementApi.createKey(data);
    },
    onSuccess: (data) => {
      toast({
        title: "API ключ создан",
        description: "Ключ успешно создан. Скопируйте его сейчас - он не будет показан снова.",
      });
      setCreatedKeys(prev => ({ ...prev, [data.id]: data.fullKey }));
      setShowKeyValue(data.id);
      setShowCreateDialog(false);
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка создания ключа",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete API key mutation
  const deleteKeyMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiManagementApi.deleteKey(id);
    },
    onSuccess: () => {
      toast({
        title: "API ключ удален",
        description: "Ключ был успешно удален.",
      });
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка удаления ключа",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleCreateKey = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
    const permissions: string[] = [];
    if (formData.get('read')) permissions.push('read');
    if (formData.get('write')) permissions.push('write');
    if (formData.get('delete')) permissions.push('delete');
    if (formData.get('admin')) permissions.push('admin');

    const orgId = formData.get('organizationId');
    const expiresAt = formData.get('expiresAt');

    const keyData: ApiKeyCreate = {
      name: formData.get('name') as string,
      organizationId: orgId ? parseInt(orgId as string) : null,
      permissions,
      rateLimitPerMinute: parseInt(formData.get('rateLimitPerMinute') as string) || 60,
      rateLimitPerHour: parseInt(formData.get('rateLimitPerHour') as string) || 1000,
      rateLimitPerDay: parseInt(formData.get('rateLimitPerDay') as string) || 10000,
      quotaRequests: parseInt(formData.get('quotaRequests') as string) || 100000,
      quotaData: parseInt(formData.get('quotaData') as string) || 1073741824,
      allowedIps: formData.get('allowedIps') ? (formData.get('allowedIps') as string).split(',').map(ip => ip.trim()).filter(ip => ip) : null,
      allowedDomains: formData.get('allowedDomains') ? (formData.get('allowedDomains') as string).split(',').map(domain => domain.trim()).filter(domain => domain) : null,
      expiresAt: expiresAt ? (expiresAt as string) : null,
      status: 'active',
    };

    createKeyMutation.mutate(keyData);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Скопировано",
        description: "Ключ скопирован в буфер обмена",
      });
    } catch (err) {
      toast({
        title: "Ошибка копирования",
        description: "Не удалось скопировать ключ",
        variant: "destructive",
      });
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'suspended': return 'bg-yellow-500';
      case 'revoked': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Активен';
      case 'suspended': return 'Приостановлен';
      case 'revoked': return 'Отозван';
      default: return 'Неизвестно';
    }
  };

  if (isLoading) {
    return <div className="p-6">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={(value) => {
        const tabValue = value as "keys" | "analytics" | "settings" | "docs";
        setActiveTab(tabValue);
        if (tabValue === "keys") {
          setSelectedKeyId(null);
        }
      }}>
        <div className="flex justify-between items-center mb-4">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-2 h-auto p-1">
            <TabsTrigger value="keys" className="text-sm py-2.5">API Ключи</TabsTrigger>
            <TabsTrigger value="analytics" className="text-sm py-2.5">Аналитика</TabsTrigger>
            <TabsTrigger value="settings" className="text-sm py-2.5">Настройки</TabsTrigger>
            <TabsTrigger value="docs" className="text-sm py-2.5">Документация API</TabsTrigger>
          </TabsList>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="ml-4 flex-shrink-0">
                <Key className="w-4 h-4 mr-2" />
                Создать API ключ
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Создать новый API ключ</DialogTitle>
              <DialogDescription>
                Создайте новый API ключ с настраиваемыми разрешениями и ограничениями
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateKey} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Название ключа</Label>
                  <Input id="name" name="name" placeholder="Мой API ключ" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organizationId">Орган управления (опционально)</Label>
                  <Input id="organizationId" name="organizationId" type="number" placeholder="ID органа управления" />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Разрешения</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" id="read" name="read" className="rounded" />
                    <Label htmlFor="read">Чтение данных</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" id="write" name="write" className="rounded" />
                    <Label htmlFor="write">Запись данных</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" id="delete" name="delete" className="rounded" />
                    <Label htmlFor="delete">Удаление данных</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" id="admin" name="admin" className="rounded" />
                    <Label htmlFor="admin">Администрирование</Label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rateLimitPerMinute">Лимит в минуту</Label>
                  <Input id="rateLimitPerMinute" name="rateLimitPerMinute" type="number" defaultValue="60" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rateLimitPerHour">Лимит в час</Label>
                  <Input id="rateLimitPerHour" name="rateLimitPerHour" type="number" defaultValue="1000" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rateLimitPerDay">Лимит в день</Label>
                  <Input id="rateLimitPerDay" name="rateLimitPerDay" type="number" defaultValue="10000" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quotaRequests">Квота запросов</Label>
                  <Input id="quotaRequests" name="quotaRequests" type="number" defaultValue="100000" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quotaData">Квота данных (байт)</Label>
                  <Input id="quotaData" name="quotaData" type="number" defaultValue="1073741824" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="allowedIps">Разрешенные IP (через запятую)</Label>
                <Input id="allowedIps" name="allowedIps" placeholder="192.168.1.1, 10.0.0.1" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="allowedDomains">Разрешенные домены (через запятую)</Label>
                <Input id="allowedDomains" name="allowedDomains" placeholder="example.com, api.example.com" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiresAt">Дата истечения (опционально)</Label>
                <Input id="expiresAt" name="expiresAt" type="datetime-local" />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={createKeyMutation.isPending}>
                  {createKeyMutation.isPending ? "Создание..." : "Создать ключ"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>

        <TabsContent value="keys" className="space-y-4 mt-4">
          {usageStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Всего запросов</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{usageStats.totalRequests?.toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Запросов сегодня</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{usageStats.requestsToday?.toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Среднее время ответа</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{usageStats.avgResponseTime}мс</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Процент ошибок</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{usageStats.errorRate}%</div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid gap-4">
            {apiKeys.map((key: ApiKey) => (
              <Card key={key.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{key.name}</CardTitle>
                      <CardDescription>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-sm bg-muted px-2 py-1 rounded">
                            {key.keyPrefix}...
                          </code>
                          <Badge className={getStatusColor(key.status)}>
                            {getStatusText(key.status)}
                          </Badge>
                        </div>
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {showKeyValue === key.id && createdKeys[key.id] && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(createdKeys[key.id])}
                          title="Копировать ключ"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedKeyId(key.id);
                          setActiveTab("analytics");
                        }}
                        title="Просмотр статистики"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteConfirmDialog({
                          isOpen: true,
                          keyId: key.id,
                          keyName: key.name
                        })}
                        title="Удалить ключ"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="font-medium">Разрешения</div>
                      <div className="text-muted-foreground">
                        {key.permissions.length > 0 ? key.permissions.join(', ') : 'Нет'}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium">Лимиты</div>
                      <div className="text-muted-foreground">
                        {key.rateLimitPerMinute}/мин, {key.rateLimitPerHour}/час
                      </div>
                    </div>
                    <div>
                      <div className="font-medium">Последнее использование</div>
                      <div className="text-muted-foreground">
                        {key.lastUsedAt 
                          ? formatDistance(new Date(key.lastUsedAt), new Date(), { locale: ru, addSuffix: true })
                          : 'Никогда'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="font-medium">Создан</div>
                      <div className="text-muted-foreground">
                        {formatDistance(new Date(key.createdAt), new Date(), { locale: ru, addSuffix: true })}
                      </div>
                    </div>
                  </div>
                  
                  {key.allowedIps && key.allowedIps.length > 0 && (
                    <div className="mt-3">
                      <div className="text-sm font-medium">Разрешенные IP:</div>
                      <div className="text-sm text-muted-foreground">{key.allowedIps.join(', ')}</div>
                    </div>
                  )}
                  
                  {key.allowedDomains && key.allowedDomains.length > 0 && (
                    <div className="mt-2">
                      <div className="text-sm font-medium">Разрешенные домены:</div>
                      <div className="text-sm text-muted-foreground">{key.allowedDomains.join(', ')}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Аналитика API</CardTitle>
              <CardDescription>
                Статистика использования API и производительность
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedKeyId && keyStats ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">
                      Статистика ключа: {apiKeys.find((key: ApiKey) => key.id === selectedKeyId)?.name}
                    </h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedKeyId(null);
                        setActiveTab("keys");
                      }}
                    >
                      Назад к списку
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{keyStats.requestsToday || 0}</div>
                      <div className="text-sm text-muted-foreground">Запросов сегодня</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{formatBytes(keyStats.dataUsageToday || 0)}</div>
                      <div className="text-sm text-muted-foreground">Данных сегодня</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{Math.round(keyStats.avgResponseTime || 0)}мс</div>
                      <div className="text-sm text-muted-foreground">Среднее время ответа</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{Math.round(keyStats.errorRate || 0)}%</div>
                      <div className="text-sm text-muted-foreground">Процент ошибок</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Выберите API ключ для просмотра детальной статистики</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Настройки API</CardTitle>
              <CardDescription>
                Глобальные настройки безопасности и производительности API
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Включить логирование API</Label>
                    <p className="text-sm text-muted-foreground">
                      Записывать все API запросы для аудита
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Требовать HTTPS</Label>
                    <p className="text-sm text-muted-foreground">
                      Отклонять запросы по незащищенному соединению
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Автоматическая блокировка подозрительной активности</Label>
                    <p className="text-sm text-muted-foreground">
                      Временно блокировать ключи при обнаружении аномалий
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="docs" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Документация API</CardTitle>
              <CardDescription>
                Руководство по использованию API для интеграции с системой
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Authentication Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Аутентификация</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Для доступа к API используйте заголовок Authorization с вашим API ключом:
                </p>
                <div className="bg-muted p-3 rounded-md font-mono text-sm">
                  <code>Authorization: Bearer YOUR_API_KEY</code>
                </div>
              </div>

              {/* Base URL Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Базовый URL</h3>
                <div className="bg-muted p-3 rounded-md font-mono text-sm">
                  <code>{window.location.origin}/api</code>
                </div>
              </div>

              {/* Objects API Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Работа с объектами</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Получить список объектов</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-green-600">GET</div>
                      <code>/objects</code>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Получить объект по ID</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-green-600">GET</div>
                      <code>/objects/:id</code>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Создать новый объект</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-blue-600">POST</div>
                      <code>/objects</code>
                    </div>
                    <div className="mt-2 bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-muted-foreground">Пример запроса:</div>
                      <pre>{`{
  "name": "Новый объект",
  "description": "Описание объекта",
  "typeId": 1,
  "organizationId": 4,
  "status": "active"
}`}</pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Geometries API Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Работа с геометрией</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Получить геометрию объекта</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-green-600">GET</div>
                      <code>/objects/:id/geometries</code>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Добавить геометрию</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-blue-600">POST</div>
                      <code>/geometries</code>
                    </div>
                    <div className="mt-2 bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-muted-foreground">Пример GeoJSON точки:</div>
                      <pre>{`{
  "objectId": 1,
  "type": "Point",
  "name": "Координата объекта",
  "geojson": {
    "type": "Point",
    "coordinates": [37.6173, 55.7558]
  }
}`}</pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search API Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Поиск объектов</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Поиск по тексту</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-green-600">GET</div>
                      <code>/objects/search?query=текст&type=название</code>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Пространственный поиск</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm">
                      <div className="text-green-600">GET</div>
                      <code>/objects/spatial?bounds=37.1,55.1,37.9,55.9</code>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Codes Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Коды ответов</h3>
                <div className="space-y-2">
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <code>200</code>
                    <span className="text-sm">Успешный запрос</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <code>201</code>
                    <span className="text-sm">Ресурс создан</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <code>400</code>
                    <span className="text-sm">Некорректный запрос</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <code>401</code>
                    <span className="text-sm">Не авторизован</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <code>403</code>
                    <span className="text-sm">Доступ запрещен</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <code>404</code>
                    <span className="text-sm">Ресурс не найден</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <code>500</code>
                    <span className="text-sm">Внутренняя ошибка сервера</span>
                  </div>
                </div>
              </div>

              {/* Rate Limiting Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Ограничения запросов</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Каждый API ключ имеет лимиты на количество запросов:
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <span>Запросов в минуту</span>
                    <code>60</code>
                  </div>
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <span>Запросов в час</span>
                    <code>1000</code>
                  </div>
                  <div className="flex justify-between p-2 bg-muted rounded">
                    <span>Запросов в день</span>
                    <code>10000</code>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  При превышении лимитов сервер вернет код ответа 429 (Too Many Requests).
                </p>
              </div>

              {/* cURL Examples Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Примеры с cURL</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Получить список объектов</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm overflow-x-auto">
                      <pre>{`curl -X GET "${window.location.origin}/api/objects" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}</pre>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Создать новый объект</h4>
                    <div className="bg-muted p-3 rounded-md font-mono text-sm overflow-x-auto">
                      <pre>{`curl -X POST "${window.location.origin}/api/objects" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Тестовый объект",
    "description": "Описание объекта",
    "typeId": 1,
    "organizationId": 4,
    "status": "active"
  }'`}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmDialog.isOpen} onOpenChange={(open) => 
        setDeleteConfirmDialog(prev => ({ ...prev, isOpen: open }))
      }>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Подтвердите удаление</DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить API ключ "{deleteConfirmDialog.keyName}"? 
              Это действие нельзя отменить.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirmDialog({ isOpen: false, keyId: null, keyName: '' })}
            >
              Отмена
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                if (deleteConfirmDialog.keyId) {
                  deleteKeyMutation.mutate(deleteConfirmDialog.keyId);
                  setDeleteConfirmDialog({ isOpen: false, keyId: null, keyName: '' });
                }
              }}
            >
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}